/**
 * Booking Appointments Data
 *
 * This file is the single source of truth for all bookable appointments.
 * Jane URLs are stored inline with appointment definitions.
 *
 * Why file-based data?
 * - Appointments are stable business truth that change infrequently
 * - Version-controlled and reviewable via git
 * - No runtime dependencies (no database, no CMS, no external API)
 * - Easy to read, edit, and reason about
 * - Type-safe at compile time
 */

// ============================================================
// Types
// ============================================================

export type LocationId = "noosa" | "maroochydore"

export type LocationPricing = {
  locationId: LocationId
  price: number
  janeUrl: string
}

export type BookingAppointment = {
  id: string
  name: string
  description: string
  duration: number // scheduled length in minutes
  locations: LocationPricing[]
}

export type ServicePillar = {
  id: string
  name: string
  initial: BookingAppointment
  return: BookingAppointment
  extended?: BookingAppointment // Optional - only General Health has this
}

// ============================================================
// Location Display Names
// ============================================================

export const LOCATIONS: Record<LocationId, string> = {
  noosa: "Noosa Clinic",
  maroochydore: "Maroochydore Clinic",
}

// ============================================================
// Jane URL Base Paths
// ============================================================

const JANE_NOOSA = "https://raintree.janeapp.com.au/locations/raintree-chinese-medicine-noosa-clinic/book#"
const JANE_MAROOCHYDORE = "https://raintree.janeapp.com.au/locations/raintree-chinese-medicine-maroochydore-clinic/book#"

// ============================================================
// Phone Consultation (Direct Link)
// ============================================================

export const PHONE_CONSULTATION_URL = "https://raintree.janeapp.com.au/locations/raintree-chinese-medicine-online-phone-consultations/book#/staff_member/1/treatment/29"

// ============================================================
// 1. General Health
// ============================================================

export const GENERAL_HEALTH: ServicePillar = {
  id: "general-health",
  name: "General Health",

  initial: {
    id: "general-health-initial",
    name: "Initial Appointment",
    description:
      "Comprehensive initial consultation for Chinese Medicine and Acupuncture. " +
      "Includes a full health intake, acupuncture treatment, and Chinese herbal " +
      "medicine consultation if desired. Please allow 60-75min. Custom herbal " +
      "formulas (if prescribed) are not included in the appointment fee.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/1/treatment/5`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/1/treatment/5`,
      },
    ],
  },

  return: {
    id: "general-health-return",
    name: "Return Appointment",
    description:
      "Return treatment for Chinese Medicine and Acupuncture. Includes a review " +
      "consultation, acupuncture treatment and herbal medicine review if desired. " +
      "Please allow up to 60min. Herbal medicines (if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/1/treatment/6`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/1/treatment/20`,
      },
    ],
  },

  extended: {
    id: "general-health-extended",
    name: "Extended Appointment",
    description:
      "Extended Chinese Medicine and Acupuncture appointment. Your 90-minute " +
      "appointment includes a comprehensive health checkup, acupuncture treatment " +
      "and may include Chinese herbal medicine consultation, cupping therapy, moxa, " +
      "massage or extended dietary or lifestyle advice. This appointment is useful " +
      "for patients with a complex health history or those who prefer more time. " +
      "Herbal medicines (if prescribed) are not included.",
    duration: 90,
    locations: [
      {
        locationId: "noosa",
        price: 220,
        janeUrl: `${JANE_NOOSA}/discipline/1/treatment/3`,
      },
      {
        locationId: "maroochydore",
        price: 220,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/1/treatment/3`,
      },
    ],
  },
}

// ============================================================
// 2. Preconception Care & Fertility Support
// ============================================================

export const FERTILITY: ServicePillar = {
  id: "fertility",
  name: "Preconception Care & Fertility Support",

  initial: {
    id: "fertility-initial",
    name: "Initial Appointment",
    description:
      "Comprehensive initial consultation for Chinese Medicine and Acupuncture for " +
      "preconception care & fertility support. Includes a comprehensive Chinese Medicine " +
      "consultation, acupuncture treatment, and Chinese herbal medicine consultation if desired. " +
      "Please allow 60-75min. Custom herbal formulas (if prescribed) are not included.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/2/treatment/1`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/2/treatment/1`,
      },
    ],
  },

  return: {
    id: "fertility-return",
    name: "Return Appointment",
    description:
      "Return appointment for Chinese Medicine and Acupuncture preconception care & " +
      "fertility support. Includes a review consultation, acupuncture treatment and " +
      "herbal medicine review if desired. Please allow up to 60min. Herbal medicines " +
      "(if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/2/treatment/4`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/2/treatment/21`,
      },
    ],
  },
}

// ============================================================
// 3. Pregnancy & Postpartum
// ============================================================

export const PREGNANCY: ServicePillar = {
  id: "pregnancy",
  name: "Pregnancy & Postpartum",

  initial: {
    id: "pregnancy-initial",
    name: "Initial Appointment",
    description:
      "Comprehensive initial consultation for Chinese Medicine and Acupuncture for " +
      "Pregnancy Support. Includes a comprehensive Chinese Medicine consultation, " +
      "acupuncture treatment, and Chinese herbal medicine consultation if desired. " +
      "Please allow 60-75min.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/5/treatment/12`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/5/treatment/12`,
      },
    ],
  },

  return: {
    id: "pregnancy-return",
    name: "Return Appointment",
    description:
      "Return appointment for Chinese Medicine and Acupuncture for pregnancy support. " +
      "Includes a review consultation, acupuncture treatment and herbal medicine review " +
      "if desired. Please allow up to 60min. Herbal medicines (if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/5/treatment/11`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/5/treatment/22`,
      },
    ],
  },
}

// ============================================================
// 4. Menstrual Support & Hormonal Health
// ============================================================

export const MENSTRUAL: ServicePillar = {
  id: "menstrual",
  name: "Menstrual Support & Hormonal Health",

  initial: {
    id: "menstrual-initial",
    name: "Initial Appointment",
    description:
      "Comprehensive initial consultation for Chinese Medicine and Acupuncture for " +
      "menstrual and hormonal support, including perimenopause & menopause support. " +
      "Includes a comprehensive Chinese Medicine consultation, acupuncture treatment, " +
      "and Chinese herbal medicine consultation if desired. Please allow 60-75min.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/6/treatment/7`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/6/treatment/7`,
      },
    ],
  },

  return: {
    id: "menstrual-return",
    name: "Return Appointment",
    description:
      "Return appointment for Chinese Medicine and Acupuncture for menstrual and " +
      "hormonal support, including perimenopause & menopause support. Includes a review " +
      "consultation, acupuncture treatment and herbal medicine review if desired. " +
      "Please allow up to 60min. Herbal medicines (if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/6/treatment/8`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/6/treatment/23`,
      },
    ],
  },
}

// ============================================================
// 5. Stress, Sleep & Digestive Balance
// ============================================================

export const STRESS_SLEEP: ServicePillar = {
  id: "stress-sleep",
  name: "Stress, Sleep & Digestive Balance",

  initial: {
    id: "stress-sleep-initial",
    name: "Initial Appointment",
    description:
      "Care for the nervous system and digestion, including stress, burnout, sleep " +
      "difficulties and IBS-type symptoms. Your initial appointment includes a comprehensive " +
      "Chinese Medicine consultation, acupuncture treatment, and Chinese herbal medicine " +
      "consultation if desired. Please allow 60-75min.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/7/treatment/14`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/7/treatment/14`,
      },
    ],
  },

  return: {
    id: "stress-sleep-return",
    name: "Return Appointment",
    description:
      "Return appointment for Chinese Medicine and Acupuncture for stress, sleep and " +
      "digestive balance. Includes a review consultation, acupuncture treatment and " +
      "herbal medicine review if desired. Please allow up to 60min. Herbal medicines " +
      "(if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/7/treatment/16`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/7/treatment/28`,
      },
    ],
  },
}

// ============================================================
// 6. Ongoing Women's Health & Long-Term Care
// ============================================================

export const WOMENS_HEALTH: ServicePillar = {
  id: "womens-health",
  name: "Ongoing Women's Health & Long-Term Care",

  initial: {
    id: "womens-health-initial",
    name: "Initial Appointment",
    description:
      "Comprehensive initial consultation for Chinese Medicine and Acupuncture for " +
      "women's health and long-term care. Your initial appointment includes a comprehensive " +
      "Chinese Medicine consultation, acupuncture treatment, and Chinese herbal medicine " +
      "consultation if desired. Please allow 60-75min.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/8/treatment/17`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/8/treatment/17`,
      },
    ],
  },

  return: {
    id: "womens-health-return",
    name: "Return Appointment",
    description:
      "Return appointment for Chinese Medicine and Acupuncture for ongoing health and " +
      "long-term care. Includes a review consultation, acupuncture treatment and herbal " +
      "medicine review if desired. Please allow up to 60min. Herbal medicines " +
      "(if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/8/treatment/18`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/8/treatment/19`,
      },
    ],
  },
}

// ============================================================
// 7. Other Concerns
// ============================================================

export const OTHER: ServicePillar = {
  id: "other",
  name: "Other Concerns",

  initial: {
    id: "other-initial",
    name: "Initial Appointment",
    description:
      "A comprehensive initial consultation for Chinese Medicine and Acupuncture that can " +
      "address other concerns: musculoskeletal pain (joint, muscle, nerve), headaches or " +
      "migraines, general stress-related symptoms, or supportive care during and after " +
      "cancer treatment. Please allow 60-75min. Custom herbal formulas (if prescribed) " +
      "are not included.",
    duration: 75,
    locations: [
      {
        locationId: "noosa",
        price: 165,
        janeUrl: `${JANE_NOOSA}/discipline/9/treatment/25`,
      },
      {
        locationId: "maroochydore",
        price: 165,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/9/treatment/25`,
      },
    ],
  },

  return: {
    id: "other-return",
    name: "Return Appointment",
    description:
      "Return treatment for Chinese Medicine and Acupuncture for other concerns: " +
      "musculoskeletal pain, headaches or migraines, general stress-related symptoms, " +
      "or supportive care during and after cancer treatment. Please allow up to 60min. " +
      "Herbal medicines (if prescribed) are not included.",
    duration: 60,
    locations: [
      {
        locationId: "noosa",
        price: 120,
        janeUrl: `${JANE_NOOSA}/discipline/9/treatment/26`,
      },
      {
        locationId: "maroochydore",
        price: 130,
        janeUrl: `${JANE_MAROOCHYDORE}/discipline/9/treatment/27`,
      },
    ],
  },
}

// ============================================================
// All Service Pillars
// ============================================================

export const SERVICE_PILLARS: ServicePillar[] = [
  GENERAL_HEALTH,
  FERTILITY,
  PREGNANCY,
  MENSTRUAL,
  STRESS_SLEEP,
  WOMENS_HEALTH,
  OTHER,
]

// ============================================================
// Helpers
// ============================================================

export function getLocationPricing(
  appointment: BookingAppointment,
  locationId: LocationId
): LocationPricing | undefined {
  return appointment.locations.find((loc) => loc.locationId === locationId)
}

export function formatPrice(price: number): string {
  return `$${price}`
}

export function formatDuration(minutes: number): string {
  return `${minutes}min`
}
